import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { ROUTES } from 'consts';
import { useAuthResetPasswordMutation } from 'store/apis/auth.api';

interface UseAuthResetPasswordFormManagerProps {
  token?: string;
  email?: string;
  next?: (data?: any) => void;
}

export const useAuthResetPasswordFormManager = ({
  token = '',
  email = '',
  next = () => {},
}: UseAuthResetPasswordFormManagerProps) => {
  const navigate = useNavigate();
  const [resetPassword, { isLoading, isSuccess, isError, error }] = useAuthResetPasswordMutation();

  const initialValues = {
    password: '',
    passwordConfirm: '',
  };

  const validationSchema = Yup.object({
    password: Yup.string().required('Password is required').min(6, 'Password must be at least 6 characters'),
    passwordConfirm: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Please make sure your passwords match')
      .required('Confirm password is required'),
  });

  const formManager = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const { password, passwordConfirm: password_confirmation } = values;

      resetPassword({
        token,
        email,
        password,
        password_confirmation,
      });
    },
  });

  useEffect(() => {
    if (isError) {
      toast.error((error as any)?.data?.payload?.message);

      navigate(`/${ROUTES.AUTH}/${ROUTES.AUTH_FORGOT_PASSWORD}`);
    }

    if (!isLoading && isSuccess && !isError) {
      toast.success('Your password has been successfully updated.');

      next();
    }
  }, [isLoading, isSuccess, isError]);

  return [
    {
      formManager,
      loading_submit: isLoading,
    },
  ] as [
    {
      formManager: FormikProps<typeof initialValues>;
      loading_submit: boolean;
    },
  ];
};
